import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  doc, getDoc,
  collection, query, where, limit, getDocs,
} from "firebase/firestore";
import { db } from "../firebase";
import { subscribeAuth, signOut } from "../services/auth";
import {
  requestContact, subscribeMyRequestFor,
} from "../services/contactRequests";

// Public profile view mounted at /u/:username. Resolves the username
// → uid via the reservation collection `Usernames/{name}`, then loads
// the underlying app_users doc plus any Pop Market storefront / buyer
// listing tied to that uid. Rendered lightweight — this is the target
// of clickable @username links inside the community forum, so the
// priority is fast context: display name, avatar, role, contact CTA.

const ROLE_BADGES = {
  seller: { label: "💎 SELLER", color: "#4de37c", bg: "rgba(43,181,98,0.14)" },
  buyer:  { label: "🎯 BUYER",  color: "#6bb6ff", bg: "rgba(60,140,220,0.16)" },
  both:   { label: "🔁 BOTH",   color: "#e3a857", bg: "rgba(227,168,87,0.14)" },
};

export default function UserProfile() {
  const { username } = useParams();
  const [state, setState] = useState({ loading: true });
  const [viewer, setViewer] = useState(null);
  useEffect(() => subscribeAuth(setViewer), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setState({ loading: true });
      try {
        // Try exact-case doc first (matches app's CamelCase format
        // like AgileWolf1). Fall back to case-insensitive lookup via
        // the lowercaseUsername field so old lowercase links keep
        // working. If neither hits, it's a 404.
        const raw = String(username || "");
        let data = null;
        let docKey = raw;
        const exact = await getDoc(doc(db, "Usernames", raw));
        if (exact.exists()) {
          data = exact.data();
        } else {
          const alt = await getDocs(query(
              collection(db, "Usernames"),
              where("lowercaseUsername", "==", raw.toLowerCase()),
              limit(1),
          )).catch(() => ({ docs: [] }));
          if (alt.docs?.[0]) {
            data = alt.docs[0].data();
            docKey = alt.docs[0].id;
          }
        }
        if (!data) {
          if (!cancelled) setState({ loading: false, notFound: true });
          return;
        }
        const uid = data.uid;
        if (!uid) {
          if (!cancelled) setState({ loading: false, notFound: true });
          return;
        }
        // Public data comes from the Usernames doc (readable by all).
        // The private app_users doc is attempted opportunistically —
        // it succeeds only when the viewer IS the owner and adds no
        // fields we depend on for rendering.
        const publicProfile = data;
        const [appSnap, sellerSnap, buyerSnap] = await Promise.all([
          getDoc(doc(db, "app_users", uid)).catch(() => ({ exists: () => false })),
          getDocs(query(collection(db, "pop_market_sellers"),
              where("paUid", "==", uid), limit(1))).catch(() => ({ docs: [] })),
          getDocs(query(collection(db, "pop_market_buyers"),
              where("paUid", "==", uid), limit(1))).catch(() => ({ docs: [] })),
        ]);
        if (cancelled) return;
        const appUser = appSnap.exists() ? appSnap.data() : null;
        const sellerDoc = sellerSnap.docs?.[0];
        const buyerDoc = buyerSnap.docs?.[0];
        setState({
          loading: false,
          username: docKey,
          publicProfile,
          appUser,
          sellerHandle: sellerDoc?.id || null,
          seller: sellerDoc ? sellerDoc.data() : null,
          buyerHandle: buyerDoc?.id || null,
          buyer: buyerDoc ? buyerDoc.data() : null,
        });
      } catch (e) {
        if (!cancelled) setState({ loading: false, error: e.message || String(e) });
      }
    })();
    return () => { cancelled = true; };
  }, [username]);

  if (state.loading) {
    return <div className="container" style={{ paddingTop: 40 }}>Loading…</div>;
  }
  if (state.notFound) {
    return (
      <div className="container" style={{ maxWidth: 480, paddingTop: 40, textAlign: "center" }}>
        <h1 style={{ marginTop: 0 }}>No such user</h1>
        <p className="muted">@{username} isn't registered on Pop Market.</p>
        <Link to="/">← Back to directory</Link>
      </div>
    );
  }
  if (state.error) {
    return <div className="container" style={{ paddingTop: 40 }}>⚠️ {state.error}</div>;
  }

  const {
    publicProfile, appUser, sellerHandle, seller, buyerHandle, buyer,
  } = state;
  // Prefer publicProfile so anonymous viewers see the full profile;
  // fall back to appUser (owner view) or seller listing avatar.
  const role = publicProfile?.popMarketRole || appUser?.popMarketRole || null;
  const badge = role ? ROLE_BADGES[role] : null;
  const whatsapp = publicProfile?.popMarketWhatsapp
      || appUser?.popMarketContact?.whatsapp
      || null;
  const displayName = publicProfile?.displayName
      || appUser?.displayName
      || `@${state.username}`;
  const photoURL = publicProfile?.photoURL
      || appUser?.photoURL
      || seller?.avatarUrl
      || null;
  // Viewer is the owner when their auth uid matches the uid the
  // username reservation resolves to — that unlocks Edit / Sign out.
  const isOwner = !!(viewer && publicProfile?.uid && viewer.uid === publicProfile.uid);

  return (
    <div className="container" style={{ maxWidth: 640, paddingBottom: 60 }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 16,
        padding: "20px 4px", borderBottom: "1px solid rgba(255,255,255,0.08)",
        flexWrap: "wrap",
      }}>
        <div style={{
          width: 72, height: 72, borderRadius: "50%",
          overflow: "hidden", background: "rgba(255,255,255,0.06)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 32, color: "#7a7d86", flexShrink: 0,
        }}>
          {photoURL
              ? <img src={photoURL} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : "👤"}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{
            fontSize: 20, fontWeight: 800, color: "#eaeaef",
            wordBreak: "break-word",
          }}>
            {displayName}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, color: "#9ca0a8" }}>@{state.username}</span>
            {badge && (
              <span style={{
                fontSize: 10, fontWeight: 900, letterSpacing: 1.2,
                padding: "2px 8px", borderRadius: 999,
                background: badge.bg, color: badge.color,
                border: `1px solid ${badge.color}44`,
              }}>
                {badge.label}
              </span>
            )}
          </div>
        </div>
        {isOwner && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Link
              to="/me/edit"
              style={{
                padding: "8px 14px", borderRadius: 8,
                background: "#4c6ef5", color: "#fff",
                fontWeight: 800, fontSize: 12, letterSpacing: 0.6,
                textDecoration: "none",
              }}
            >
              ✎ Edit profile
            </Link>
            <button
              type="button"
              onClick={() => signOut()}
              style={{
                padding: "8px 14px", borderRadius: 8,
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.14)",
                color: "#c8ced5",
                fontWeight: 700, fontSize: 12, letterSpacing: 0.6,
                cursor: "pointer",
              }}
            >
              Sign out
            </button>
          </div>
        )}
      </div>

      {!isOwner && whatsapp && (
        <section style={{ marginTop: 20 }}>
          <h3 style={{ fontSize: 12, letterSpacing: 2, color: "#7a7d86", margin: "0 0 10px" }}>
            REACH THEM
          </h3>
          <ContactRequestButton
            viewer={viewer}
            ownerUid={publicProfile?.uid}
            ownerUsername={state.username}
            ownerDisplayName={displayName}
          />
        </section>
      )}
      {!isOwner && !whatsapp && (
        <section style={{ marginTop: 20 }}>
          <h3 style={{ fontSize: 12, letterSpacing: 2, color: "#7a7d86", margin: "0 0 10px" }}>
            REACH THEM
          </h3>
          <p className="muted" style={{ fontSize: 13 }}>
            No public contact yet.
          </p>
        </section>
      )}

      {(sellerHandle || buyerHandle) && (
        <section style={{ marginTop: 24 }}>
          <h3 style={{ fontSize: 12, letterSpacing: 2, color: "#7a7d86", margin: "0 0 10px" }}>
            LISTINGS ON POP MARKET
          </h3>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {sellerHandle && (
              <Link
                to={`/seller/${sellerHandle}`}
                style={linkPill("#4de37c")}
              >
                💎 View storefront →
              </Link>
            )}
            {buyerHandle && (
              <Link
                to={`/buyer/${buyerHandle}`}
                style={linkPill("#6bb6ff")}
              >
                🎯 View buy requirement →
              </Link>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

function linkPill(tint) {
  return {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "10px 14px", borderRadius: 10,
    background: `${tint}18`, color: tint,
    border: `1px solid ${tint}44`,
    fontWeight: 800, fontSize: 13, textDecoration: "none",
  };
}

// Consent-gated WhatsApp reveal. Viewer sends a request → owner
// approves in their inbox → button flips to a live WhatsApp link
// with the actual number. State comes from a live subscription on
// the request doc so approval reflects immediately.
function ContactRequestButton({ viewer, ownerUid, ownerUsername, ownerDisplayName }) {
  const [req, setReq] = useState(null);       // null | request doc
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!viewer || !ownerUid) return;
    setSubscribed(true);
    return subscribeMyRequestFor(
        { fromUid: viewer.uid, toUid: ownerUid, channel: "whatsapp" },
        setReq,
    );
  }, [viewer?.uid, ownerUid]);

  const onRequest = async () => {
    if (!viewer || busy) return;
    setBusy(true); setErr(null);
    try {
      await requestContact({ toUsername: ownerUsername });
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  if (!viewer) {
    return (
      <Link to="/signin" style={pillStyle("#4c6ef5")}>
        🔒 Sign in to request WhatsApp
      </Link>
    );
  }

  if (!subscribed) {
    return <span style={pillStyle("#7a7d86", true)}>Loading…</span>;
  }

  if (!req) {
    return (
      <>
        <button
          type="button"
          onClick={onRequest}
          disabled={busy}
          style={pillStyle("#25D366", false, busy)}
        >
          {busy ? "Requesting…" : `💬 Request ${ownerDisplayName.split(" ")[0]}'s WhatsApp`}
        </button>
        {err && <div style={{ marginTop: 6, fontSize: 12, color: "#ff6f61" }}>⚠️ {err}</div>}
      </>
    );
  }

  if (req.status === "pending") {
    return (
      <span style={pillStyle("#e3a857", true)}>
        ⏳ Waiting for @{ownerUsername} to approve…
      </span>
    );
  }

  if (req.status === "denied") {
    return (
      <span style={pillStyle("#ff6f61", true)}>
        ✗ @{ownerUsername} declined to share their contact
      </span>
    );
  }

  if (req.status === "approved" && req.sharedContact?.whatsapp) {
    const wa = req.sharedContact.whatsapp;
    return (
      <a
        href={`https://wa.me/${wa.replace(/[^\d]/g, "")}?text=${encodeURIComponent(
            `Hi ${ownerDisplayName}, saw your profile on Pop Market.`)}`}
        target="_blank"
        rel="noopener noreferrer"
        style={pillStyle("#25D366")}
      >
        💬 WhatsApp {ownerDisplayName.split(" ")[0]} — {wa}
      </a>
    );
  }
  return null;
}

function pillStyle(tint, ghost = false, disabled = false) {
  return {
    display: "inline-flex", alignItems: "center", gap: 8,
    padding: "10px 16px", borderRadius: 10,
    background: ghost ? "transparent" : tint,
    color: ghost ? tint : "#0d1017",
    border: ghost ? `1px dashed ${tint}66` : "none",
    fontWeight: 800, fontSize: 13, letterSpacing: 0.4,
    textDecoration: "none",
    cursor: disabled ? "not-allowed" : (ghost ? "default" : "pointer"),
    opacity: disabled ? 0.6 : 1,
  };
}
